import { type DefaultTheme, useTheme } from 'styled-components';
import { Grid } from '../Core';
import { Icon, IconName } from '../Icons';
import { AlertBannerWrapper, Content, Stripes } from './styles';

import { mix } from 'polished';
import type { ReactNode } from 'react';
import { setAlpha } from '../../styles';
import { AlertVariants } from '../Alert/types';

export type AlertBannerVariants =
  | AlertVariants.Negative
  | AlertVariants.Positive
  | AlertVariants.Warning
  | AlertVariants.Notice;

export interface AlertBannerProps {
  variant: AlertBannerVariants;
  children: ReactNode;
  icon?: IconName;
  showStripes?: boolean;
  /** When true, overrides variant colors with neutral gray background and content. */
  neutralColors?: boolean;
}

export function AlertBanner({
  variant,
  children,
  icon: propsIcon,
  showStripes = true,
  neutralColors = false,
}: AlertBannerProps) {
  const theme = useTheme();
  const { backgroundColor, iconColor, icon: defaultIcon } = getAlertBannerColor(variant, theme);
  const icon = propsIcon ?? defaultIcon;
  const resolvedBackgroundColor = neutralColors ? setAlpha(0.12, theme.colors.gray['100']) : backgroundColor;
  const contentColor = neutralColors ? setAlpha(0.8, theme.colors.gray['100']) : undefined;
  const horizontalPadding = showStripes ? theme.spacing4 : theme.spacing12;

  return (
    <Grid
      w="100%"
      gridTemplateColumns={showStripes ? '1fr auto 1fr' : '1fr'}
      position="relative"
      background={resolvedBackgroundColor}
    >
      {showStripes && <Stripes backgroundPosition="right top" />}
      <Content $contentColor={contentColor} $horizontalPadding={horizontalPadding}>
        <Icon icon={icon} color={iconColor} />
        {children}
      </Content>
      {showStripes && <Stripes backgroundPosition="left top" />}
      {showStripes && <AlertBannerWrapper $backgroundColor={resolvedBackgroundColor} />}
    </Grid>
  );
}

const getAlertBannerColor = (
  variant: AlertBannerVariants,
  theme: DefaultTheme
): { backgroundColor: string; icon: IconName; iconColor: string } => {
  switch (variant) {
    case AlertVariants.Positive:
      return {
        backgroundColor: mix(0.4, theme.colors.green.default, theme.colors.gray.main),
        iconColor: theme.colors.green.lighten,
        icon: IconName.InformationCircleSolid,
      };
    case AlertVariants.Negative:
      return {
        backgroundColor: mix(0.6, theme.colors.red.default, theme.colors.gray.main),
        iconColor: theme.colors.red.lighten,
        icon: IconName.ExclamationSolid,
      };
    case AlertVariants.Warning:
      return {
        backgroundColor: theme.colors.yellow.lighten,
        iconColor: theme.colors.gray['000'],
        icon: IconName.ExclamationSolid,
      };
    case AlertVariants.Notice:
      return {
        backgroundColor: theme.colors.gray.main,
        iconColor: theme.colors.yellow.lighten,
        icon: IconName.ExclamationSolid,
      };
  }
};
